import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Typography } from '../typography/Typography';
import { ButtonBase } from '../button/ButtonBase';
import { useStep } from './StepContext';
import { useProgressStepper } from './ProgressStepperContext';
import { WarningFilled } from '../private/icons/WarningFilled';
import { ErrorFilled } from '../private/icons/ErrorFilled';

/**
 * `StepButton` is the container for the icon or text to be shown for each `Step` in a `ProgressStepper`.
 * It's the only interactive element on non-linear progress steppers.
 *
 * See icons for `StepButton` in the
 * [Icon Library](https://brand.homedepot.com/site/knapsack-nucleus/latest/pattern/icons?subPageId=DESIGN).
 *
 * Related components: [ProgressStepper](#progressstepper), [Step](#step), [StepLabel](#steplabel),
 * [StepContent](#stepcontent).
 *
 * Usage:
 *
 * ```jsx
 * import { StepButton } from '@one-thd/sui-atomic-components';
 * ```
 */
const StepButton = React.forwardRef((props, ref) => {
  const {
    icon: IconProp,
    align: alingProp,
    onClick: onClickProp,
    text: textProp,
    ...rest
  } = props;

  const {
    align: alignStep,
    active,
    completed,
    status,
    disabled,
    index
  } = useStep();
  const { nonLinear } = useProgressStepper();
  const align = alingProp || alignStep;
  const interactive = nonLinear && !disabled;
  const onClick = interactive ? onClickProp : null;

  const isError = status === 'error';
  const isWarning = status === 'warning';
  const isCompleted = completed && !active && !status;

  const touchTargetClasses = 'after:sui-cursor-pointer after:sui-h-11 after:sui-w-11 after:sui-absolute';
  const focusStyles = 'focus-visible:sui-outline-none focus-visible:sui-bg-focus focus-visible:sui-border-0 focus-visible:sui-ring-0 focus-visible:sui-text-primary focus-visible:sui-fill-primary';
  const hoverStyles = 'hover:sui-bg-brand hover:sui-text-inverse hover:sui-ring-4 hover:sui-ring-input-hover-accent hover:sui-border-0 hover:sui-fill-inverse';
  const nonDisabledClasses = !disabled && classNames({
    'sui-bg-primary': active && !isError && nonLinear,
    'sui-border-solid sui-border-2': active && nonLinear,
    'sui-text-primary': !completed && !status && !active,
    'sui-bg-primary sui-border-2 sui-border-solid sui-border-primary sui-fill-primary': !active && !completed && !status,
    'sui-bg-success-strong sui-text-inverse sui-fill-inverse': isCompleted || (active && !nonLinear && !isError && !isWarning),
    'sui-border-primary': active && !status && !nonLinear,
    'sui-border-strongest sui-fill-primary': active && !status && nonLinear,
    'sui-border-warning sui-bg-warning sui-fill-primary': active && isWarning,
    'sui-border-danger sui-bg-danger sui-fill-inverse': active && isError,
    'sui-bg-warning sui-fill-primary': isWarning && !active,
    'sui-bg-danger sui-text-inverse': isError && !active,
  });
  const buttonClasses = classNames(
    'sui-order-first sui-relative sui-rounded-full sui-shrink-0 sui-w-8 sui-h-8 sui-inline-flex sui-items-center sui-justify-center sui-z-10',
    nonDisabledClasses,
    {
      'sui-self-end': align === 'end',
      'sui-self-start': align === 'start',
      'sui-self-center': align === 'center',
      'sui-bg-subtle sui-text-inactive sui-cursor-not-allowed sui-fill-medium': disabled,
      'sui-shadow-md': active && !nonLinear,
      [touchTargetClasses]: interactive,
      [focusStyles]: interactive,
      [hoverStyles]: interactive,
    }
  );

  let Icon = IconProp;
  if (status === 'warning') {
    Icon = WarningFilled;
  } else if (status === 'error') {
    Icon = ErrorFilled;
  }

  const component = nonLinear ? 'button' : 'div';
  const text = textProp || (index + 1);

  return (
    <ButtonBase
      unstyled
      type={component === 'button' ? 'button' : null}
      className={buttonClasses}
      onClick={onClick}
      disabled={disabled}
      component={component}
      ref={ref}
      {...rest}
    >
      {Icon ? (
        <Icon size="small" color="inherit" />
      ) : (
        <Typography component="span" weight="bold" color={isCompleted || (active && !nonLinear) ? 'inverse' : 'primary'}>{text}</Typography>
      )}
    </ButtonBase>
  );
});

StepButton.displayName = 'StepButton';

StepButton.propTypes = {
  /**
   * The aligment of the button inside Step container.
   * If not provided, it uses the calculated alignment passed down by Step
   */
  align: PropTypes.oneOf(['center', 'start', 'end']),
  /**
   * The icon of the component.
   */
  icon: PropTypes.elementType,
  /**
   * The event for clicking StepButton
   */
  onClick: PropTypes.func,
  /**
   * The digit representing the step ordinal number.
   * If not icon or text is provided, defaults to the Step's number
   */
  text: PropTypes.string
};

export { StepButton };
